// src/components/JarvislySimpleComponents/SimpleLoadingPage.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import React from 'react';
import { Grid, Spin } from 'antd';
import { printGlobalStateToConsole } from 'configs/ConfigIndex';
import translateX from 'utils/translateX';
import { useThemeStore } from 'stores/theme';
import { TEMPLATE } from 'constants/ThemeConstant';
import utils from 'utils/utils';
import useZustand from 'hooks/useZustand';
import {
  StyledContentCenter,
  StyledCover,
  StyledTitleCenter,
} from 'layouts/SharedComponents/StyledComponents';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const { useBreakpoint } = Grid;
const DefaultIcon = null;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const SimpleLoadingPage = ({ visible, icon, title, translucid }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const storedLoadingApp = useZustand('boot', 'loadingApp', true, true);
  const storedLoadingModule = useZustand('boot', 'loadingModule', true, true);
  const storedLoadingPage = useZustand('boot', 'loadingPage', true, true);
  const storedNavCollapsed = useThemeStore(s => s.navCollapsed);
  const storedNavType = useThemeStore(s => s.navType);
  const storedDirection = useThemeStore(s => s.direction);
  const storedAppActivated = useZustand('app', 'appActivated');
  const currentTheme = useZustand('theme', 'currentTheme');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const screens = utils.getBreakPoint(useBreakpoint());
  const isMobile = screens.length === 0 ? false : !screens.includes('lg');

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const showBase = typeof visible === 'boolean' ? visible : !!storedLoadingPage?.visible;
  const show = showBase && !storedLoadingApp?.visible && !storedLoadingModule?.visible;
  const isTranslucid = translucid ?? !!storedLoadingPage?.translucid;
  // const isTranslucid =
  //   typeof translucid === 'boolean' ? translucid : !!storedLoadingPage?.translucid;

  const iconNode =
    icon === false
      ? null
      : (icon ??
        (storedLoadingPage?.icon === false ? null : (storedLoadingPage?.icon ?? DefaultIcon)));
  const finalTitle = title ?? storedLoadingPage?.title;
  const tip = finalTitle ? translateX(finalTitle) : null;

  // Calculate padding based on nav state and storedSubscription
  const getLayoutGutter = () => {
    if (['top', 'tile'].includes(storedNavType) || isMobile || !storedAppActivated) return 0;
    return storedNavCollapsed ? TEMPLATE.SIDE_NAV_COLLAPSED_WIDTH : TEMPLATE.SIDE_NAV_WIDTH;
  };

  const paddingLeft = getLayoutGutter();
  const direction = storedDirection || TEMPLATE.DIR_LTR;

  // ─────────────────────────────────────────────────────────────────────────────
  // 9. UI
  // ─────────────────────────────────────────────────────────────────────────────
  if (!show) return null;
  return (
    <StyledCover
      translucid={isTranslucid}
      currentTheme={currentTheme}
      paddingLeft={paddingLeft}
      direction={direction}
      onDoubleClick={printGlobalStateToConsole}
      style={{
        zIndex: 9999,
        paddingLeft: direction === TEMPLATE.DIR_LTR ? paddingLeft : 0,
        paddingRight: direction === TEMPLATE.DIR_RTL ? paddingLeft : 0,
      }}
    >
      <StyledContentCenter>
        {iconNode ? <Spin size="large" indicator={iconNode} /> : <Spin size="large" />}
        {tip ? <StyledTitleCenter currentTheme={currentTheme}>{translateX(tip)}</StyledTitleCenter> : null}
      </StyledContentCenter>
    </StyledCover>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default SimpleLoadingPage;

// *******************************************************************************
// HELPERS
// *******************************************************************************
